import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';

import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { TableState } from 'components/data-table/types';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import type {
  DraftAssetRow,
  DraftAssetsQueryParams,
  DraftAssetsResponse,
} from '../types';
import { draftAssetFilterStateToParams, sortParamsFromState } from '../utils';
import { AssetsTableView } from './assets-table-view';

/** Props for {@link Step2AssetsTable}. */
export interface Step2AssetsTableProps {
  /** Active draft id. The fetch fires on mount/draftId-change. */
  draftId: string;
  /** Show every owning entity of a Tract: the draft came from an asset list. */
  allEntities?: boolean;
}

/**
 * Read-only assets query for Step 2's review accordion. POSTs to the
 * same endpoint as Step 1's query atom, but lives as a separate atom
 * with its own abort controller — so paging or filtering Step 1 can't
 * cancel Step 2's in-flight fetch (and vice versa) and Step 1's
 * `currentDraftSummaryAtom` data isn't clobbered by Step 2's
 * filtered-to-selected response.
 *
 * Intentionally does NOT write `currentDraftSummaryAtom`. The summary
 * panel on Step 1 derives from the universe view (no `selected: true`
 * filter); overwriting it from a `selected: true` fetch would shift
 * the breakdowns to "selected only," which is a different question.
 */
const [, draftAssetsReadOnlyQueryLoadableAtom] = mutationAtomWithLoadable<
  DraftAssetsResponse,
  [DraftAssetsQueryParams]
>(
  (() => {
    let controller: AbortController | null = null;
    return async (_get, _set, params: DraftAssetsQueryParams) => {
      controller?.abort();
      controller = new AbortController();
      const { draftId, ...body } = params;
      const url = `/queries/packages/drafts/${draftId}/assets`;
      const response = await request.post<
        DraftAssetsResponse,
        { errors: string[] }
      >(url, body, controller);
      if (response.type === 'success') return response.data;
      if (response.type === 'abort') return new Promise<never>(() => {});
      if (response.type === 'error' && response.data?.errors?.length) {
        throw new Error(response.data.errors.join(', '));
      }
      throw new Error('Failed to fetch draft assets');
    };
  })()
);

/**
 * Step 2 read-only assets table. Shows only assets currently on the
 * draft (BE-filtered via `selected: true`). No selection column, no
 * pin column, no PATCH wiring — review-screen presentation only.
 *
 * Reuses {@link AssetsTableView} for columns, sub-tract expand, and the
 * DataTable shell, so the column set stays in lock-step with Step 1.
 *
 * @param {Step2AssetsTableProps} props - draft id
 * @return {JSX.Element} The read-only assets table
 */
export const Step2AssetsTable: FC<Step2AssetsTableProps> = ({
  draftId,
  allEntities = false,
}) => {
  const [queryState, fetchAssets] = useAtom(
    draftAssetsReadOnlyQueryLoadableAtom
  );

  const loadData = useCallback(
    (params: TableState) => {
      fetchAssets({
        draftId,
        // `selected: true` is the read-only contract — baked in here, not
        // exposed as a prop, so callers can't accidentally widen the scope.
        selected: true,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...sortParamsFromState(params.sorting),
        ...draftAssetFilterStateToParams(params.columnFilters),
      });
    },
    [draftId, fetchAssets]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes. Match Step 1's
  // default-sort key so the read-only review opens in the same order.
  useEffect(() => {
    loadData({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      globalFilter: '',
      sorting: [{ id: 'name', desc: false }],
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    });
  }, [draftId, loadData]);

  const data = useMemo<DraftAssetRow[]>(() => {
    if (queryState.state === 'hasData' && queryState.data) {
      return queryState.data.results;
    }
    return [];
  }, [queryState]);
  const totalCount =
    queryState.state === 'hasData' && queryState.data
      ? queryState.data.totalCount
      : 0;
  const isLoading = queryState.state === 'loading';

  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : null;

  return (
    <AssetsTableView
      data={data}
      totalCount={totalCount}
      isLoading={isLoading}
      errorMessage={errorMessage}
      loadData={loadData}
      searchPlaceholder="Search by Tract Name"
      allEntities={allEntities}
    />
  );
};
